/**
 * Admin -> Settings (eskiz 24). Faqat owner. Saqlashda faqat o'zgargan kalitlar + version yuboriladi,
 * 409 bo'lsa joriy holat qayta yuklanadi (02-yechim, 8-band).
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import { X } from "lucide-react";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useMe } from "@/features/auth/hooks";
import type { Role } from "@/features/auth/types";
import { useSettings, useUpdateSettings } from "@/features/admin/api";
import type { AppSettingsFull, Approver, AssignmentEndPolicy, DuplicatePolicy, SettingsPatch } from "@/features/admin/types";
import { AdminLayout } from "./AdminLayout";
import { IntegrationsPanel } from "./IntegrationsPanel";
import { DocumentsSettingsPanel } from "./DocumentsSettingsPanel";
import { ProblemsPanel } from "./ProblemsPanel";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import { Checkbox, Field, Input, NumberInput, Select } from "@/components/ui/field";
import { EmptyState, ErrorState, PageLoading } from "@/components/ui/misc";
import { toast } from "@/components/ui/toast";

type Section = "statuses" | "assignment" | "kpi" | "expenses" | "security" | "documents" | "problems" | "integrations";
type Draft = Omit<AppSettingsFull, "version">;

// Bo'lim nomi: settings:sections.<key>
const SECTIONS: { key: Section; tone?: "budget" }[] = [
  { key: "statuses" },
  { key: "assignment" },
  { key: "kpi", tone: "budget" },
  { key: "expenses", tone: "budget" },
  { key: "security" },
  { key: "documents" },
  { key: "problems" },
  { key: "integrations" },
];

export function SettingsPage() {
  const { t } = useTranslation("settings");
  const me = useMe();
  const isOwner = me.data?.role === "owner";
  const settings = useSettings(isOwner);
  if (!me.data) return <PageLoading />;
  if (!isOwner) {
    return (
      <AdminLayout title={t("title")}>
        <EmptyState title={t("ownerOnlyTitle")} description={t("ownerOnlyDesc")} />
      </AdminLayout>
    );
  }
  if (settings.isPending) {
    return (
      <AdminLayout title={t("title")}>
        <PageLoading />
      </AdminLayout>
    );
  }
  if (settings.isError) {
    return (
      <AdminLayout title={t("title")}>
        <ErrorState message={settings.error.message} onRetry={() => settings.refetch()} />
      </AdminLayout>
    );
  }
  return <SettingsEditor settings={settings.data} />;
}

function stripVersion(s: AppSettingsFull): Draft {
  const { version: _v, ...rest } = s;
  return rest;
}

function SettingsEditor({ settings }: { settings: AppSettingsFull }) {
  const { t } = useTranslation("settings");
  // ?section=integrations -- masalan Google'dan qaytganda shu bo'lim ochiladi
  const [params] = useSearchParams();
  const [section, setSection] = useState<Section>(() => {
    const fromUrl = params.get("section");
    return SECTIONS.some((s) => s.key === fromUrl) ? (fromUrl as Section) : "assignment";
  });
  const [draft, setDraft] = useState<Draft>(() => stripVersion(settings));
  const update = useUpdateSettings();

  // Server o'zgarsa (boshqa oyna, 409 dan keyin qayta yuklash) va biz hali tahrirlamagan bo'lsak, yangilaymiz
  useEffect(() => {
    setDraft((d) => (JSON.stringify(d) === JSON.stringify(stripVersion(settings)) ? d : d));
  }, [settings]);

  const changed = useMemo(() => {
    const base = stripVersion(settings);
    const out: Partial<Draft> = {};
    (Object.keys(draft) as (keyof Draft)[]).forEach((k) => {
      if (JSON.stringify(draft[k]) !== JSON.stringify(base[k])) (out as Record<string, unknown>)[k] = draft[k];
    });
    return out;
  }, [draft, settings]);
  const dirty = Object.keys(changed).length > 0;

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => setDraft((d) => ({ ...d, [key]: value }));

  async function save() {
    try {
      await update.mutateAsync({ ...changed, version: settings.version } as SettingsPatch);
      toast.success(t("saved"), t("savedDesc"));
    } catch (err) {
      if (err instanceof ApiError && err.isConflict) {
        toast.error(t("conflict"));
        setDraft(stripVersion((err.current as AppSettingsFull) ?? settings));
      } else toast.error(err instanceof ApiError ? err.message : t("saveFailed"));
    }
  }

  return (
    <AdminLayout title={t("title")} subtitle={t("subtitle")}>
      <div className="grid grid-cols-[200px_1fr] gap-5">
        <nav className="flex flex-col gap-0.5">
          {SECTIONS.map((s) => (
            <button
              key={s.key}
              onClick={() => setSection(s.key)}
              className={cn(
                "rounded-[10px] px-3 py-2 text-left text-[13px] text-ink-soft hover:bg-fill",
                section === s.key && "bg-primary-soft font-semibold text-ink",
                s.tone === "budget" && section !== s.key && "text-budget",
              )}
            >
              {t(`sections.${s.key}`)}
            </button>
          ))}
        </nav>
        <div className="space-y-4">
          {section === "statuses" && <StatusesPanel draft={draft} set={set} />}
          {section === "assignment" && <AssignmentPanel draft={draft} set={set} />}
          {section === "kpi" && <KpiPanel draft={draft} set={set} />}
          {section === "expenses" && <ExpensesPanel draft={draft} set={set} />}
          {section === "security" && <SecurityPanel draft={draft} set={set} />}
          {section === "documents" && <DocumentsSettingsPanel draft={draft} set={set} />}
          {section === "problems" && <ProblemsPanel draft={draft} set={set} />}
          {section === "integrations" && <IntegrationsPanel />}
          {section !== "integrations" && (
          <div className="flex items-center justify-end gap-2 border-t border-line pt-4">
            {dirty && <span className="mr-auto text-[12px] text-muted">{t("unsaved", { count: Object.keys(changed).length })}</span>}
            <Button onClick={() => setDraft(stripVersion(settings))} disabled={!dirty}>
              {t("common:actions.cancel")}
            </Button>
            <Button variant="primary" onClick={save} loading={update.isPending} disabled={!dirty}>
              {t("common:actions.saveChanges")}
            </Button>
          </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}

export type PanelProps = { draft: Draft; set: <K extends keyof Draft>(key: K, value: Draft[K]) => void };

// ---- Panellar ----

function StatusesPanel({ draft, set }: PanelProps) {
  const { t } = useTranslation("settings");
  return (
    <>
      <Card>
        <CardHeader title={t("statuses.title")} subtitle={t("statuses.subtitle")} />
        <CardBody>
          <Field label={t("statuses.staleDays")} hint={t("statuses.staleDaysHint")} className="max-w-[220px]">
            <NumberInput min={1} value={draft.pipeline_stale_days} onChange={(e) => set("pipeline_stale_days", Number(e.target.value))} />
          </Field>
        </CardBody>
      </Card>
      <Card>
        <CardHeader title={t("statuses.rejection")} subtitle={t("statuses.rejectionDesc")} />
        <CardBody>
          <ListEditor items={draft.rejection_reasons} onChange={(v) => set("rejection_reasons", v)} placeholder={t("statuses.newReason")} />
        </CardBody>
      </Card>
      <Card>
        <CardHeader title={t("statuses.lost")} subtitle={t("statuses.lostDesc")} />
        <CardBody>
          <ListEditor items={draft.lost_reasons} onChange={(v) => set("lost_reasons", v)} placeholder={t("statuses.newReason")} />
        </CardBody>
      </Card>
      <Card>
        <CardHeader title={t("statuses.quit")} subtitle={t("statuses.quitDesc")} />
        <CardBody>
          <ListEditor items={draft.quit_reasons} onChange={(v) => set("quit_reasons", v)} placeholder={t("statuses.newReason")} />
        </CardBody>
      </Card>
    </>
  );
}

function AssignmentPanel({ draft, set }: PanelProps) {
  const { t } = useTranslation("settings");
  const policies: { value: AssignmentEndPolicy; label: string }[] = [
    { value: "extend_if_active", label: t("assignment.extend", { days: draft.assignment_activity_window_days }) },
    { value: "return_to_pool", label: t("assignment.returnToPool") },
    { value: "ask_manager", label: t("assignment.askManager") },
  ];
  return (
    <>
      <Card>
        <CardHeader title={t("assignment.title")} />
        <CardBody className="space-y-4">
          <div className="flex items-end gap-3">
            <Field label={t("assignment.period")} className="w-[120px]">
              <NumberInput min={1} value={draft.assignment_days} onChange={(e) => set("assignment_days", Number(e.target.value))} />
            </Field>
            <span className="pb-2 text-[13px]">{t("assignment.days")}</span>
            <span className="pb-2 text-[12px] text-muted">{t("assignment.periodHint")}</span>
          </div>
          <div>
            <div className="mb-2 text-[13px] font-semibold">{t("assignment.whenEnds")}</div>
            <div className="space-y-2">
              {policies.map((p) => (
                <label key={p.value} className="flex items-center gap-2 text-[13px]">
                  <input type="radio" name="assignment_end_policy" className="accent-primary" checked={draft.assignment_end_policy === p.value} onChange={() => set("assignment_end_policy", p.value)} />
                  {p.label}
                </label>
              ))}
            </div>
            <div className="mt-3 flex items-center gap-3">
              <Field label={t("assignment.window")} className="w-[160px]">
                <NumberInput min={1} value={draft.assignment_activity_window_days} onChange={(e) => set("assignment_activity_window_days", Number(e.target.value))} />
              </Field>
            </div>
          </div>
          <p className="text-[12px] text-muted">{t("assignment.transferHint")}</p>
        </CardBody>
      </Card>
      <Card>
        <CardHeader title={t("assignment.duplicates")} />
        <CardBody className="space-y-3">
          {/* Telefon va CDL -- bazada noyob, o'chirib bo'lmaydi (backend drivers.services.HARD_DUPLICATES) */}
          <p className="text-[13px] text-ink-soft">{t("assignment.always")}</p>
          <Toggle label={t("assignment.phoneSecondary")} hint={t("assignment.phoneSecondaryHint")} checked={draft.duplicate_check_phone_secondary} onChange={(v) => set("duplicate_check_phone_secondary", v)} />
          <Toggle label={t("assignment.email")} checked={draft.duplicate_check_email} onChange={(v) => set("duplicate_check_email", v)} />
          <Toggle label={t("assignment.name")} hint={t("assignment.nameHint")} checked={draft.duplicate_check_name} onChange={(v) => set("duplicate_check_name", v)} />
          <Field label={t("assignment.onMatch")} hint={t("assignment.policyHint")} className="max-w-[460px]">
            <Select value={draft.duplicate_policy} onChange={(e) => set("duplicate_policy", e.target.value as DuplicatePolicy)}>
              <option value="block_show_owner">{t("assignment.blockShowOwner")}</option>
              <option value="allow_with_warning">{t("assignment.allowWarning")}</option>
              <option value="block_recruiters">{t("assignment.blockRecruiters")}</option>
            </Select>
          </Field>
        </CardBody>
      </Card>
    </>
  );
}

function KpiPanel({ draft, set }: PanelProps) {
  const { t } = useTranslation("settings");
  return (
    <>
      <Card className="border-budget">
        <CardHeader title={t("kpi.title")} subtitle={t("kpi.subtitle")} />
        <CardBody className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("kpi.ruleName")}>
              <Input value={draft.bonus_rule_name} onChange={(e) => set("bonus_rule_name", e.target.value)} />
            </Field>
            <Field label={t("kpi.currency")}>
              <Select value={draft.bonus_currency} onChange={(e) => set("bonus_currency", e.target.value)}>
                <option value="USD">USD</option>
                <option value="UZS">UZS</option>
              </Select>
            </Field>
          </div>
          <div className="grid grid-cols-4 gap-3">
            <NumberField label={t("kpi.perHire")} value={draft.bonus_per_hire} onChange={(v) => set("bonus_per_hire", v)} />
            <NumberField label={t("kpi.stayed30")} value={draft.bonus_per_30_days} onChange={(v) => set("bonus_per_30_days", v)} />
            <NumberField label={t("kpi.stayed60")} value={draft.bonus_per_60_days} onChange={(v) => set("bonus_per_60_days", v)} />
            <NumberField label={t("kpi.stayed90")} value={draft.bonus_per_90_days} onChange={(v) => set("bonus_per_90_days", v)} />
          </div>
          <Field label={t("kpi.monthlyGoal")} hint={t("kpi.monthlyGoalHint")} className="max-w-[260px]">
            <NumberInput min={0} value={draft.monthly_hire_goal} onChange={(e) => set("monthly_hire_goal", Number(e.target.value))} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("kpi.approver")}>
              <Select value={draft.bonus_approver} onChange={(e) => set("bonus_approver", e.target.value as Approver)}>
                <option value="branch_manager">{t("common:roles.branch_manager")}</option>
                <option value="owner">{t("common:roles.owner")}</option>
              </Select>
            </Field>
            <Field label={t("kpi.retention")} hint={t("kpi.retentionHint")}>
              <Input value={draft.retention_check_days.join(", ")} onChange={(e) => set("retention_check_days", parseDays(e.target.value))} />
            </Field>
          </div>
        </CardBody>
      </Card>
      <Card>
        <CardHeader title={t("kpi.expiry")} subtitle={t("kpi.expiryDesc")} />
        <CardBody>
          <Field label={t("kpi.daysBefore")} hint={t("kpi.daysBeforeHint")} className="max-w-[260px]">
            <Input value={draft.expiry_reminder_days.join(", ")} onChange={(e) => set("expiry_reminder_days", parseDays(e.target.value))} />
          </Field>
        </CardBody>
      </Card>
    </>
  );
}

function ExpensesPanel({ draft, set }: PanelProps) {
  const { t } = useTranslation("settings");
  return (
    <>
      <Card className="border-budget">
        <CardHeader title={t("expenses.title")} subtitle={t("expenses.subtitle")} />
        <CardBody>
          <RateListEditor
            items={draft.expense_categories}
            rates={draft.commission_rates.out}
            onChange={(items, rates) => {
              set("expense_categories", items);
              set("commission_rates", { ...draft.commission_rates, out: rates });
            }}
            placeholder={t("expenses.newCategory")}
          />
        </CardBody>
      </Card>
      <Card className="border-budget">
        <CardHeader title={t("income.title")} subtitle={t("income.subtitle")} />
        <CardBody>
          <RateListEditor
            items={draft.income_categories}
            rates={draft.commission_rates.in}
            onChange={(items, rates) => {
              set("income_categories", items);
              set("commission_rates", { ...draft.commission_rates, in: rates });
            }}
            placeholder={t("income.newCategory")}
          />
        </CardBody>
      </Card>
      <Card>
        <CardHeader title={t("expenses.rules")} />
        <CardBody className="space-y-3">
          <Toggle label={t("expenses.receipt")} hint={t("expenses.receiptHint")} checked={draft.expense_receipt_required} onChange={(v) => set("expense_receipt_required", v)} />
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("expenses.approver")}>
              <Select value={draft.expense_approver} onChange={(e) => set("expense_approver", e.target.value as Approver)}>
                <option value="owner">{t("common:roles.owner")}</option>
                <option value="branch_manager">{t("common:roles.branch_manager")}</option>
              </Select>
            </Field>
            <Field label={t("expenses.alert")} hint={t("expenses.alertHint")}>
              <NumberInput min={1} value={draft.budget_alert_threshold} onChange={(e) => set("budget_alert_threshold", Number(e.target.value))} />
            </Field>
          </div>
          {/* Tasdiqsiz chegara (2026-10-18, Bito'dagi kabi): shu summagacha menejer xarajati o'zi tasdiqlanadi */}
          <Field label={t("expenses.autoLimit")} hint={t("expenses.autoLimitHint")}>
            <div className="flex gap-2">
              <NumberInput min={0} decimals={2} value={draft.expense_auto_approve_limit} onChange={(e) => set("expense_auto_approve_limit", Number(e.target.value))} />
              <Select value={draft.expense_auto_approve_currency} onChange={(e) => set("expense_auto_approve_currency", e.target.value as "USD" | "UZS")} className="w-[120px] shrink-0">
                <option value="USD">USD</option>
                <option value="UZS">UZS</option>
              </Select>
            </div>
          </Field>
        </CardBody>
      </Card>
      {/* Mijoz jadvali: "Payment date" = ishga chiqqan kun + 14 */}
      <Card>
        <CardHeader title={t("payments.title")} subtitle={t("payments.subtitle")} />
        <CardBody>
          <Field label={t("payments.dueDays")} hint={t("payments.dueDaysHint")} className="max-w-[260px]">
            <NumberInput min={0} max={365} value={draft.placement_fee_due_days} onChange={(e) => set("placement_fee_due_days", Number(e.target.value))} />
          </Field>
        </CardBody>
      </Card>
    </>
  );
}

function SecurityPanel({ draft, set }: PanelProps) {
  const { t } = useTranslation("settings");
  const roles: Role[] = ["owner", "branch_manager", "recruiter"];
  // 2FA siyosati: belgilangan rollar 2FA'siz ishlay olmaydi va uni o'chira olmaydi (backend: accounts/middleware.py)
  function toggleRole(role: Role, on: boolean) {
    const next = new Set(draft.twofa_required_roles);
    if (on) next.add(role);
    else next.delete(role);
    set("twofa_required_roles", roles.filter((r) => next.has(r)));
  }
  return (
    <>
      <Card>
        <CardHeader title={t("security.twofa")} subtitle={t("security.twofaDesc")} />
        <CardBody className="space-y-2">
          {roles.map((r) => (
            <Checkbox key={r} label={t(`common:roles.${r}`)} checked={draft.twofa_required_roles.includes(r)} onChange={(e) => toggleRole(r, e.target.checked)} />
          ))}
        </CardBody>
      </Card>
      <Card>
        <CardHeader title={t("security.sessions")} />
        <CardBody>
          <div className="grid grid-cols-3 gap-3">
            <NumberField label={t("security.sessionHours")} value={draft.session_hours} onChange={(v) => set("session_hours", v)} min={1} />
            <NumberField label={t("security.maxAttempts")} value={draft.login_max_attempts} onChange={(v) => set("login_max_attempts", v)} min={1} />
            <NumberField label={t("security.lockMinutes")} value={draft.login_lockout_minutes} onChange={(v) => set("login_lockout_minutes", v)} min={1} />
          </div>
          <p className="mt-3 text-[12px] text-muted">{t("security.applyNext")}</p>
        </CardBody>
      </Card>
      <Card>
        <CardHeader title={t("security.public")} subtitle={t("security.publicDesc")} />
        <CardBody className="space-y-3">
          <Toggle label={t("security.applyForm")} checked={draft.apply_form_enabled} onChange={(v) => set("apply_form_enabled", v)} />
          <Toggle label={t("security.portalPhone")} checked={draft.carrier_portal_show_phone} onChange={(v) => set("carrier_portal_show_phone", v)} />
          <Field label={t("security.companyName")} className="max-w-[320px]">
            <Input value={draft.company_name} onChange={(e) => set("company_name", e.target.value)} />
          </Field>
        </CardBody>
      </Card>
    </>
  );
}

// ---- Kichik bo'laklar ----

function parseDays(value: string): number[] {
  return value
    .split(",")
    .map((s) => Number(s.trim()))
    .filter((n) => Number.isFinite(n) && n > 0);
}

function NumberField({ label, value, onChange, min = 0 }: { label: string; value: number; onChange: (v: number) => void; min?: number }) {
  return (
    <Field label={label}>
      <NumberInput min={min} value={value} onChange={(e) => onChange(Number(e.target.value))} />
    </Field>
  );
}

function Toggle({ label, hint, checked, onChange }: { label: string; hint?: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center justify-between gap-3 text-[13px]">
      <span>
        {label}
        {hint && <span className="ml-2 text-[11px] text-muted">· {hint}</span>}
      </span>
      <button type="button" role="switch" aria-checked={checked} onClick={() => onChange(!checked)} className={cn("relative h-[22px] w-10 shrink-0 rounded-full transition-colors", checked ? "bg-primary" : "bg-border")}>
        <span className={cn("absolute top-[2px] h-[18px] w-[18px] rounded-full bg-surface transition-all", checked ? "left-[20px]" : "left-[2px]")} />
      </button>
    </label>
  );
}

/** Matnli ro'yxat: qo'shish va o'chirish (sabablar, kategoriyalar) */
/** Foiz maydoni: yozish paytida matn o'zida turadi ("1." yo'qolmaydi), tashqariga raqam (bo'sh/0 -- foizsiz) */
function RateInput({ value, onChange, label }: { value: number | undefined; onChange: (value: number | undefined) => void; label: string }) {
  const [text, setText] = useState(value ? String(value) : "");
  useEffect(() => {
    setText((cur) => (Number(cur || 0) === (value ?? 0) ? cur : value ? String(value) : ""));
  }, [value]);
  const bad = text !== "" && !(Number(text) >= 0 && Number(text) < 100);
  return (
    <NumberInput
      decimals={2}
      min={0}
      value={text}
      placeholder="0"
      aria-label={label}
      aria-invalid={bad || undefined}
      className={cn("w-[88px]", bad && "border-danger")}
      onChange={(e) => {
        const v = e.target.value;
        setText(v);
        const n = Number(v);
        onChange(v !== "" && n > 0 && n < 100 ? n : undefined);
      }}
    />
  );
}

/** Kategoriyalar + ixtiyoriy komissiya foizi (2026-10-25): kirimda ayriladi, chiqimda qo'shiladi (finance.services) */
function RateListEditor({ items, rates, onChange, placeholder }: { items: string[]; rates: Record<string, number>; onChange: (items: string[], rates: Record<string, number>) => void; placeholder?: string }) {
  const { t } = useTranslation("settings");
  const [value, setValue] = useState("");
  const without = (name: string) => Object.fromEntries(Object.entries(rates).filter(([k]) => k !== name));
  function add() {
    const v = value.trim();
    if (!v || items.includes(v)) return;
    onChange([...items, v], rates);
    setValue("");
  }
  return (
    <div>
      {items.length === 0 ? (
        <span className="text-[12px] text-muted">{t("list.empty")}</span>
      ) : (
        <div className="max-w-[420px]">
          <div className="flex items-center gap-2 pb-1 text-[11px] font-medium text-muted">
            <span className="flex-1" />
            <span className="w-[88px]">{t("list.rateHead")}</span>
            <span className="w-6" />
          </div>
          {items.map((item) => (
            <div key={item} className="flex items-center gap-2 border-t border-line py-1.5">
              <span className="min-w-0 flex-1 truncate text-[13px]">{item}</span>
              <RateInput value={rates[item]} label={t("list.rateAria", { item })} onChange={(n) => onChange(items, n === undefined ? without(item) : { ...rates, [item]: n })} />
              <button type="button" onClick={() => onChange(items.filter((i) => i !== item), without(item))} className="grid h-6 w-6 place-items-center rounded-full text-muted hover:bg-fill-hover hover:text-ink" aria-label={t("list.remove", { item })}>
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="mt-3 flex gap-2">
        <Input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          placeholder={placeholder}
          className="max-w-[280px]"
        />
        <Button onClick={add} disabled={!value.trim()}>
          {t("list.add")}
        </Button>
      </div>
    </div>
  );
}

function ListEditor({ items, onChange, placeholder }: { items: string[]; onChange: (items: string[]) => void; placeholder?: string }) {
  const { t } = useTranslation("settings");
  const [value, setValue] = useState("");
  function add() {
    const v = value.trim();
    if (!v || items.includes(v)) return;
    onChange([...items, v]);
    setValue("");
  }
  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {items.map((item) => (
          <span key={item} className="inline-flex items-center gap-1 rounded-full bg-fill py-1 pr-1 pl-3 text-[12px]">
            {item}
            <button type="button" onClick={() => onChange(items.filter((i) => i !== item))} className="rounded-full p-0.5 text-muted hover:bg-fill-hover hover:text-ink" aria-label={t("list.remove", { item })}>
              <X size={12} />
            </button>
          </span>
        ))}
        {items.length === 0 && <span className="text-[12px] text-muted">{t("list.empty")}</span>}
      </div>
      <div className="mt-3 flex gap-2">
        <Input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
          placeholder={placeholder}
          className="max-w-[280px]"
        />
        <Button onClick={add} disabled={!value.trim()}>
          {t("list.add")}
        </Button>
      </div>
    </div>
  );
}
